import React, { createContext, useContext, useEffect, useState } from 'react';

import { useAuth } from 'contexts/auth';
import accountRequests from 'services/account';
import courseRequests from 'services/course';
import groupRequests from 'services/group';
import { message } from 'shell/components';
import { GROUP_ROLE, TYPES } from 'utils/constants';
import { getUserPreferredFullName } from 'utils/helper';

const StudentRoleContext = createContext();
const useStudent = () => useContext(StudentRoleContext);

const StudentRoleProvider = (props) => {
    const { auth, currentUser, setAuth } = useAuth();

    const [groupUsers, setGroupUsers] = useState(null);
    const [groupAccounts, setGroupAccounts] = useState(null);
    const [contribution, setContribution] = useState();
    const [distribution, setDistribution] = useState();
    const [groupAuthorities, setGroupAuthorities] = useState([]);
    const [courseInstructors, setCourseInstructors] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (auth) {
            const initializeChat = async () => {
                setLoading(true);
                await Promise.all([
                    getGroupUsers(),
                    getGroupAccounts(),
                    getGroupAuthorities(),
                    listContribution(),
                    listDistribution(),
                    getCourseInstructors(),
                ]);
                setLoading(false);
            };
            initializeChat();
        }
    }, [auth]);

    const getGroupAccounts = async () => {
        if (!auth || !auth?.group_id) return [];
        const currentGroupAccounts = await groupRequests.findGroupAccounts(auth.group_id);
        setGroupAccounts(currentGroupAccounts);
        return currentGroupAccounts;
    };

    const getGroupUsers = async () => {
        if (!auth || !auth?.group_id) return [];
        const currentGroupUsers = await groupRequests.findGroupUsers(auth.group_id);
        setGroupUsers(currentGroupUsers);
        return currentGroupUsers;
    };

    const getGroupAuthorities = async () => {
        if (!auth || !auth?.group_id) return;
        const authorities = await groupRequests.findGroupAuthorities(auth.group_id);
        return setGroupAuthorities(authorities);
    };

    const listContribution = async () => {
        if (!auth || !auth?.group_id) return {};
        const contribution = await groupRequests.findGroupContribution(auth.group_id);
        setContribution(contribution);
        return contribution;
    };

    const listDistribution = async () => {
        if (!auth || !auth?.group_id) return;
        const distribution = await groupRequests.findGroupDistribution(auth.group_id);
        return setDistribution(distribution);
    };

    const getCourseInstructors = async () => {
        const instructors = await getInstructors();
        setCourseInstructors(instructors);
    };

    const getGroupAccountsByUserId = async () => {
        const accountsByIds = {};
        let accounts = groupAccounts;
        if (!groupAccounts || groupAccounts?.length === 0) accounts = await getGroupAccounts();
        accounts.map((item) => {
            accountsByIds[item.user_id] = item;
        });

        return accountsByIds;
    };

    const getActiveGroupUsers = () => {
        return groupAccounts
            .filter(({ active }) => active)
            .map(({ id: account_id, user_id }) => {
                return {
                    ...groupUsers.find((user) => user_id === user.id),
                    account_id,
                };
            });
    };

    const instructorsByAccountId = async () => {
        const getInstructors = await courseRequests.findCourseInstructors(auth.course_id);
        const result = {};
        getInstructors.map((item) => {
            result[item.owner_account_id] = item;
        });
        return result;
    };

    const getInstructors = async () => {
        return courseRequests.findCourseInstructors(auth.course_id);
    };

    const getGroupDistribution = () => distribution;

    const getDashboardGroupUsers = async () => {
        const accounts = await getGroupAccountsByUserId();
        let tempGroupUsers = await getGroupUsers();

        return tempGroupUsers.map((item) => {
            const individualContribution = contribution?.assigned[accounts[item.id].id] || {};
            const { active, ...rest } = item;
            return {
                ...rest,
                active: accounts[item.id].active,
                owner_account_id: accounts[item.id].id,
                tasks: [
                    individualContribution?.low?.total || 0,
                    individualContribution?.medium?.total || 0,
                    individualContribution?.high?.total || 0,
                ],
            };
        });
    };

    const getBothUserAndAccounts = async () => {
        const accounts = await getGroupAccountsByUserId();
        const groupMembers = await getGroupUsers();
        const result = await Promise.all(
            groupMembers.map((item) => {
                const { id, ...rest } = accounts[item.id];
                return {
                    ...item,
                    owner_account_id: id,
                    ...rest,
                };
            })
        );
        return result;
    };

    const getUsersByAccountId = async () => {
        const accounts = await getGroupAccountsByUserId();
        const result = {};
        (groupUsers || []).forEach((item) => {
            result[accounts[item.id].id] = { ...item, connections: accounts[item.id].connections };
        });
        return result;
    };

    const getAssignableGroupMembers = () => {
        return (groupAccounts || [])
            .filter(({ active }) => active)
            .map((account) => {
                const member = (groupUsers || []).find(({ id }) => id === account.user_id);

                return {
                    title: getUserPreferredFullName(member),
                    value: account.id,
                };
            });
    };

    const getUserByAccountId = (accountId) => {
        const userId = (groupAccounts || []).find(({ id }) => id === accountId)?.user_id;
        return (groupUsers || []).find(({ id }) => id === userId);
    };

    const getMemberTaskPermissions = () => {
        if (!groupAuthorities.length) return [];
        return groupAuthorities
            .find((authority) => authority.name === GROUP_ROLE.MEMBER)
            .policies.find((policy) => policy.resource === TYPES.TASK)?.actions;
    };

    const updateTeamLeader = async (newLeaderAccountId) => {
        try {
            await Promise.all(
                groupAccounts.map((account) => {
                    return accountRequests.updateAccount(account.id, {
                        group_role: account.id === newLeaderAccountId ? GROUP_ROLE.LEADER : GROUP_ROLE.MEMBER,
                    });
                })
            );
            setGroupAccounts((groupAccounts) => {
                return groupAccounts.map((account) => {
                    return {
                        ...account,
                        group_role: account.id === newLeaderAccountId ? GROUP_ROLE.LEADER : GROUP_ROLE.MEMBER,
                    };
                });
            });
            setAuth((auth) => ({
                ...auth,
                group_role: auth.owner_account_id === newLeaderAccountId ? GROUP_ROLE.LEADER : GROUP_ROLE.MEMBER,
            }));
        } catch (e) {
            console.error(e);
            message.error('Could not set team leader.');
        }
    };

    const updateTaskPermission = async (action) => {
        const currentGroupAuthorities = groupAuthorities;
        const newGroupAuthorities = groupAuthorities.map((item) => {
            if (item.name === GROUP_ROLE.MEMBER) {
                return {
                    ...item,
                    policies: item.policies.map((policy) => {
                        if (policy.resource === TYPES.TASK) {
                            return policy.actions.includes(action)
                                ? {
                                      ...policy,
                                      actions: policy.actions.filter((a) => a !== action),
                                  }
                                : {
                                      ...policy,
                                      actions: [...policy.actions, action],
                                  };
                        } else return policy;
                    }),
                };
            } else return item;
        });
        setGroupAuthorities(newGroupAuthorities);

        try {
            await groupRequests.updateGroupAuthorities(auth.group_id, newGroupAuthorities);
        } catch (e) {
            console.error(e);
            message.error('Could not update permissions.');
            setGroupAuthorities(currentGroupAuthorities);
        }
    };

    const updateSelfUserInGroup = (selfUser) => {
        setGroupUsers((prev) => {
            return prev.map((user) => {
                return user.id === currentUser.id
                    ? {
                          ...user,
                          ...selfUser,
                      }
                    : user;
            });
        });
    };

    const getGroupLeaderAccountId = () => {
        return (
            groupAccounts.find((account) => {
                return account.group_role === GROUP_ROLE.LEADER;
            })?.id || ''
        );
    };

    return (
        <StudentRoleContext.Provider
            value={{
                loading,
                groupUsers,
                groupAccounts,
                groupAuthorities,
                courseInstructors,
                getActiveGroupUsers,
                getGroupDistribution,
                getDashboardGroupUsers,
                getGroupAccountsByUserId,
                contribution,
                distribution,
                listContribution,
                listDistribution,
                getAssignableGroupMembers,
                getUserByAccountId,
                getMemberTaskPermissions,
                updateTeamLeader,
                updateTaskPermission,
                getUsersByAccountId,
                getBothUserAndAccounts,
                instructorsByAccountId,
                getInstructors,
                updateSelfUserInGroup,
                getGroupLeaderAccountId,
            }}
            {...props}
        />
    );
};

export { useStudent };
export default StudentRoleProvider;
